/**
 * The v3 campaign landing page: one template, three configs.
 *
 * Design package "Alchemiq landing pages", v1.0, 20 Oct 2026. The `.dc.html`
 * sources are the authority on every measurement here, so the styles are
 * inline and transcribed rather than moved into a stylesheet: the handoff's
 * own instruction is "use the inline styles in the sources for exact
 * measurements", and a class name in between is one more place for a value to
 * drift from the design.
 *
 * Per-page copy lives in `landingConfig.js`. Nothing in this file names an
 * audience, so a shared element cannot differ between the three pages by
 * accident.
 *
 * TWO DELIBERATE DEPARTURES from the prototype, both so the page does not
 * claim something untrue:
 *
 *  1. The example feed shows REAL stories from `GET /api/stories`, filtered by
 *     the selected category, which is the handoff's acceptance item 5. The
 *     prototype ships "Sample headline:" placeholder rows. When the fetch
 *     fails or a category has nothing, the card renders an honest empty line
 *     rather than invented headlines: the strip is captioned "Detected by
 *     Alchemiq", and `LiveStoriesPanel.jsx` already carries this rule for the
 *     same reason.
 *  2. The newsroom note box drops its last sentence, which promises that
 *     clicking a story reaches an AI draft. `README_newsroom_variant.md` marks
 *     that flow as not built and says to remove the sentence if it cannot
 *     ship.
 *
 * SUBMIT IS WIRED. It posts a real `CampaignVisit`, which records the consent
 * and starts the feed build, and the welcome email follows when the feed is
 * ready. A trial signup then hands off to `/trial/confirmation` via
 * `onTrialSubmitted`. All three audiences are trial signups since 29 October
 * 2026, when the newsroom demo branch was removed for posting submissions the
 * server rejected. This note previously said none of that had landed, which
 * stopped being true before this page was adapted for the phone.
 *
 * ONE CARVE-OUT FROM THE INLINE-STYLE POLICY ABOVE, added with the phone
 * layout: a property whose value DIFFERS at the phone breakpoint lives in
 * `LandingPageV3.css` instead, for both breakpoints, because a media query
 * cannot reach an inline style. Roughly eight properties across seven elements.
 * The rule is that no property is declared in both places, so every value
 * still has exactly one home and the reason for the inline policy survives.
 * That file explains the partition and names the trap; read it before moving
 * anything.
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';

import { relativeTime } from '../LiveStoriesPanel';
import { CATEGORY_SLUGS } from './landingConfig';
import { CAMPAIGN_SEND_WINDOW_LABEL, CAMPAIGN_SEND_WINDOW_SENTENCE } from './sendWindow';
import { createTracker } from './landingTrack';
import { STICKY_BAR_BREAKPOINT } from './breakpoints';
import LandingConsent from './LandingConsent';
import { useConsentChoice } from '../../../analytics/useConsentChoice';
import './LandingPageV3.css';

const NAVY = '#0B1E3F';
const SLATE = '#3C4A5E';
const ORANGE = '#FF6B35';
const ORANGE_HOVER = '#E8592A';
const TECH_BLUE = '#00AEEF';
const SOFT_WHITE = '#F7F9FC';
const ERROR = '#D13030';
const PILL_GREY = '#E6EBF2';
const STRIP_GREY = '#EEF1F5';
const BORDER = 'rgba(60,74,94,0.14)';
const DIVIDER = 'rgba(60,74,94,0.12)';
const INPUT_BORDER = 'rgba(60,74,94,0.3)';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STORIES_PER_CATEGORY = 4;
const MAX_TOPICS = 2;
const SUPPORT = 'support@alchemiq.ai';
const PRIVACY_URL = 'https://alchemiq.ai/privacy';

/** Hover handlers for a plain orange button. */
const orangeHover = {
  onMouseOver: (e) => { e.currentTarget.style.background = ORANGE_HOVER; },
  onMouseOut: (e) => { e.currentTarget.style.background = ORANGE; },
};

/**
 * "AP · Reuters · BBC · +4".
 *
 * `sourceLabel` in LiveStoriesPanel maps over source OBJECTS, which is what
 * `/stories` returns. The cached endpoint sends names already deduped and
 * capped, plus the true `sourceCount`, so the "+N" stays honest without
 * shipping every publisher name for every story.
 */
/** The attribution the tracker already holds for this session. */
function readUtmForSubmit() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('aq_attr') || '{}');
    return {
      source: saved.utm_source || null,
      medium: saved.utm_medium || null,
      campaign: saved.utm_campaign || null,
      content: saved.utm_content || null,
      term: saved.utm_term || null,
      /*
       * Captured since the pages shipped and, until now, dropped here: the
       * five tags were submitted and the click ids were not, so they reached
       * the browser's dataLayer and never the database. Without them no
       * conversion we record later can be handed back to the platform that
       * sold us the click.
       */
      adSetId: saved.utm_id || null,
      liFatId: saved.li_fat_id || null,
      fbclid: saved.fbclid || null,
      gclid: saved.gclid || null,
    };
  } catch {
    return {};
  }
}

function formatSources(story) {
  const names = Array.isArray(story?.sources) ? story.sources.filter(Boolean) : [];
  if (names.length === 0) return '';
  const extra = Math.max(0, Number(story?.sourceCount || names.length) - names.length);
  return extra > 0 ? `${names.join(' · ')} · +${extra}` : names.join(' · ');
}

function Chip({ label, on, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-disabled={disabled}
      style={{
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: 44,
        padding: '0 16px',
        borderRadius: 999,
        fontWeight: 600,
        fontSize: 14,
        color: on ? '#fff' : NAVY,
        border: `1px solid ${on ? ORANGE : INPUT_BORDER}`,
        background: on ? ORANGE : '#fff',
      }}
    >
      {label}
    </button>
  );
}

function Field({ id, label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label htmlFor={id} style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>
        {label}
        {required && <span aria-hidden="true" style={{ color: ORANGE, fontWeight: 700 }}> *</span>}
      </label>
      {children}
    </div>
  );
}

/*
 * Height the sticky bar is assumed to have before it has been measured, and the
 * floor the spacer never goes below.
 *
 * 84 is the design handoff's own number: 12px padding, a 52px button, 12px
 * padding, plus the border. It is kept as a floor rather than trusted outright,
 * because at 360px a long label can wrap and make the real bar taller, which is
 * what the measurement below is for.
 */
const STICKY_BAR_FALLBACK_H = 84;

/*
 * No `font` here. Font size is the one input property that differs on a phone
 * (15px to 16px, so iOS does not zoom the page on focus), so it lives in
 * `.lp-input` in the stylesheet. Every call site pairs this with
 * `className="lp-input"`. The border colour stays because it is derived from
 * validation state and is the same at every width.
 */
const inputStyle = (invalid) => ({
  minHeight: 48,
  padding: '0 14px',
  border: `1px solid ${invalid ? ERROR : INPUT_BORDER}`,
  borderRadius: 6,
  fontFamily: 'Inter,sans-serif',
  color: NAVY,
  background: '#fff',
});

/**
 * How long a submit waits for a verdict before proceeding optimistically.
 *
 * Long enough for a validation rejection, which is a round trip with no work
 * behind it, and short enough that nobody watches a spinner. See the comment in
 * `submit`.
 */
const SUBMIT_GRACE_MS = 2500;
const PENDING = Symbol('pending');
const SUBMIT_REJECTED = 'We could not accept that signup. Please check your details and try again.';

export default function LandingPageV3({ config, onTrialSubmitted }) {
  const {
    campaign, page, mode: initialMode, heroPill, h1, intro, introBold,
    noteBold, noteRest, feedCategories, topics: topicChips, topicsLabel,
    freeTextLabel, freeTextPlaceholder, faq, proof,
    trialTopicsLabel, trialFreeTextLabel,
    faqHeading, faqHeadingSize, showWhyItMatters,
    websiteUrl, websiteLabel, websiteLabelPhone, noteCta,
  } = config;

  /*
   * `mode` is now always 'trial'. The newsroom demo branch was removed on
   * 29 October 2026: it posted to the trial endpoint with `consent: false`,
   * which the server rejects outright, so every newsroom submission silently
   * failed while the page reported success. See `landingConfig.js`.
   *
   * The variable is kept because analytics has reported `conversion_type` since
   * the funnel launched, and dropping it would put a hole in that series.
   */
  const mode = initialMode;

  const [email, setEmail] = useState('');
  const [sel, setSel] = useState([]);
  const [text, setText] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [cat, setCat] = useState(feedCategories[0]);
  const [feedByCategory, setFeedByCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [open, setOpen] = useState(-1);
  const [stickyOn, setStickyOn] = useState(false);
  /*
   * Whether any field in the form has focus, i.e. whether the phone keyboard is
   * probably up. Drives `barVisible` below so the bar cannot cover the field
   * being typed into.
   */
  const [formFocused, setFormFocused] = useState(false);
  /* Measured height of the sticky bar. See the useLayoutEffect below. */
  const [barH, setBarH] = useState(STICKY_BAR_FALLBACK_H);

  const emailRef = useRef(null);
  const formRef = useRef(null);
  const barRef = useRef(null);
  const errorRef = useRef(null);
  const tracker = useMemo(() => createTracker({ campaign, page }), [campaign, page]);

  /*
   * TWO booleans, not one, and the difference is load bearing.
   *
   *   barActive   the bar belongs on this page right now, so the spacer must
   *               reserve its height and the consent bar must sit above it
   *   barVisible  the bar is actually painted
   *
   * They diverge only while a field has focus. If the spacer tracked
   * visibility, focusing a field would shrink the document by the bar's height,
   * and `goToForm` focuses the first field 400ms into a smooth scroll, so the
   * page would contract mid-flight and the scroll would land short of the form,
   * which is the one thing that button exists to do.
   */
  /*
   * ONE BAR AT A TIME, Adi 3 November 2026.
   *
   * Both of these are fixed to the bottom edge. The cookie strip used to be
   * lifted clear of the sticky CTA by a measured offset, so on a phone the two
   * stacked and took most of a short viewport. Suppressing the CTA until the
   * cookie question is answered removes the overlap rather than arranging it,
   * and the cookie bar is the one that has to go first: it is the one blocking
   * measurement of everything the visitor does next.
   *
   * Held here, not in `LandingConsent`, because `useConsentChoice` keeps its
   * own state per caller and a second copy there would never tell this one the
   * visitor had clicked.
   */
  const cookieBar = useConsentChoice();
  const barActive = stickyOn && !sent && !cookieBar.visible;
  const barVisible = barActive && !formFocused;

  const emailOk = EMAIL_RE.test(email);

  /*
   * The example feed, fetched ONCE for every category.
   *
   * This used to call `/stories?category=` keyed on `[cat]`, so a page load
   * cost one request and every pill click cost another. Worse, that endpoint's
   * in-memory cache covers none of the slugs this page uses, so each of those
   * requests ran a 10,000-document aggregate against Mongo on ad traffic.
   *
   * `/public/landing/example-feed` returns every category from a cache
   * rebuilt every two hours, so the whole strip costs one request and a pill
   * click costs nothing. The payload is about 4KB, less than the round trips
   * it replaces.
   */
  useEffect(() => {
    let live = true;
    const base = process.env.REACT_APP_BASE_URL || '/api';
    axios
      .get(`${base}/public/landing/example-feed`)
      .then(({ data }) => {
        if (!live) return;
        setFeedByCategory((data && data.categories) || {});
      })
      .catch(() => { if (live) setFeedByCategory({}); });
    return () => { live = false; };
  }, []);

  /*
   * `null` until the fetch resolves, so the card can tell "still loading" from
   * "this category genuinely has nothing", which are different sentences.
   */
  const stories = feedByCategory === null
    ? null
    : (feedByCategory[CATEGORY_SLUGS[cat]] || []).slice(0, STORIES_PER_CATEGORY);

  /* ── Page-level tracking: landing view, scroll depth, abandonment ── */
  useEffect(() => {
    tracker.track('landing_view');

    const checkSticky = () => {
      const r = formRef.current && formRef.current.getBoundingClientRect();
      const visible = !!r && r.top < window.innerHeight - 80 && r.bottom > 80;
      setStickyOn(window.innerWidth < STICKY_BAR_BREAKPOINT && !!r && !visible);
    };
    const onScroll = () => {
      checkSticky();
      /*
       * Read from the document, which is what scrolls now. The formula and the
       * rounding are deliberately unchanged from when this measured the page's
       * own scroll container, so the funnel's depth percentages stay
       * comparable either side of that change.
       */
      const el = document.scrollingElement || document.documentElement;
      if (!el || el.scrollHeight <= 0) return;
      tracker.scrolled(
        Math.round(((el.scrollTop + el.clientHeight) / el.scrollHeight) * 100),
      );
    };
    const onLeave = () => {
      if (document.visibilityState === 'hidden') tracker.abandoned();
    };
    const onPageHide = () => tracker.abandoned();
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('mailto:')) tracker.track('contact_clicked', { target: 'support_email' });
      else if (href.includes('alchemiq.ai')) tracker.track('outbound_click', { target: 'alchemiq_website' });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    /*
     * Rotation, as well as a resized desktop window. Load bearing: a 430px
     * phone turned landscape is 932px wide, which crosses BOTH thresholds at
     * once, desktop layout and no sticky bar, and without this the bar would
     * be left on screen in landscape.
     */
    window.addEventListener('resize', onScroll);
    document.addEventListener('visibilitychange', onLeave);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('click', onClick);
    const t = setTimeout(checkSticky, 500);

    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', onLeave);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('click', onClick);
    };
  }, [tracker]);

  /*
   * Measure the sticky bar rather than hardcoding its height.
   *
   * The spacer below it and the consent bar's offset both have to clear the
   * bar exactly. Those were two hand-tuned numbers (84 and 76) that agreed
   * with the bar only as long as nothing about it changed, and at 360px a long
   * label wraps and makes it taller.
   *
   * Only while `barActive`: a `display:none` element measures zero, which
   * would collapse the spacer and let the bar cover the contact band.
   */
  useLayoutEffect(() => {
    if (!barActive) return undefined;
    const el = barRef.current;
    if (!el) return undefined;

    const measure = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setBarH(h);
    };
    measure();

    // Guarded: jsdom has no ResizeObserver, and neither does every browser we
    // claim to support. The one-shot measure above is the fallback.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [barActive]);

  /*
   * Does any field in the form hold focus?
   *
   * One pair of handlers on the `<form>` covers every field, because React's
   * synthetic focus events bubble where the native ones do not.
   *
   * The `requestAnimationFrame` is not defensive padding. `focusout` fires
   * BEFORE the matching `focusin`, so tabbing from one field to the next
   * momentarily reports "nothing focused", a naive handler flashes the sticky
   * bar on every field change. Re-checking a frame later asks the question
   * once focus has actually settled.
   */
  const onFormFocus = useCallback(() => setFormFocused(true), []);
  const onFormBlur = useCallback(() => {
    const form = formRef.current;
    if (typeof requestAnimationFrame !== 'function') {
      setFormFocused(false);
      return;
    }
    requestAnimationFrame(() => {
      if (!form || !form.contains(document.activeElement)) setFormFocused(false);
    });
  }, []);
  const formFocusProps = { onFocus: onFormFocus, onBlur: onFormBlur };

  const toggleTopic = useCallback((label) => {
    const on = sel.includes(label);
    if (!on && sel.length >= MAX_TOPICS) {
      tracker.track('topic_limit_reached', { topic: label });
      setError('You can choose up to two topics. Remove one to add another.');
      return;
    }
    tracker.touch('topics');
    tracker.track('topic_selected', { topic: label, selected: !on });
    setSel((s) => (on ? s.filter((x) => x !== label) : [...s, label]));
    setError('');
  }, [sel, tracker]);

  /** The feed CTA never blocks on the limit: it replaces the second topic. */
  /*
   * `source` since 1 Nov 2026. This hardcoded `sticky_cta_clicked` because the
   * sticky bar was its only caller. It now has three, so without a source every
   * jump-to-form click would be reported as a sticky-bar click and the funnel
   * could not tell which affordance people actually use.
   */
  const goToForm = (source = 'sticky') => {
    tracker.track('cta_clicked', { source });
    const el = formRef.current;
    if (el && typeof window.scrollTo === 'function') {
      /*
       * Explicit arithmetic rather than `scrollIntoView`, because the handoff
       * specifies a 16px gap above the form and `scrollIntoView` gives no way
       * to ask for one.
       */
      const top = el.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setTimeout(() => {
      const f = emailRef.current;
      if (f) f.focus({ preventScroll: true });
    }, 400);
  };

  const takeCategory = useCallback(() => {
    tracker.advance('feed_interacted');
    tracker.track('feed_cta_clicked', { category: cat });
    setSel((s) => {
      if (s.includes(cat)) return s;
      return s.length >= MAX_TOPICS ? [s[0], cat] : [...s, cat];
    });
    setSent(false);
    /*
     * Was its own `setTimeout(100)` plus a bare `focus()`, relying on the
     * browser's default focus-scroll. That made three different scroll
     * behaviours on one page and skipped the 16px gap above the form the
     * handoff specifies. `goToForm` is the one implementation.
     */
    goToForm('category');
  }, [cat, tracker, goToForm]);

  /*
   * Show the error and make sure the visitor can see it.
   *
   * On a phone the submit button is frequently the only part of the form on
   * screen when it is pressed, so without this the press looks like it did
   * nothing. Deferred a frame because the error line does not exist in the DOM
   * until the state change that `setError` triggers has rendered.
   *
   * Every call is optional: jsdom has no `scrollIntoView`, and neither does
   * every embedded browser.
   */
  const revealError = (message) => {
    setError(message);
    if (typeof requestAnimationFrame !== 'function') return;
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    /*
     * An ATTEMPT, which is not a submission.
     *
     * This used to push `form_submitted` here, before any validation ran, so
     * the event fired on every press of the button including the ones that
     * went nowhere. Anyone building a conversion on the obvious-sounding name
     * would have counted people who never got past the consent tick, and the
     * funnel's own `stage_reached` had the same fault: `advance` is forward
     * only, so one failed press marked the visitor as having reached the
     * `form_submitted` stage for the rest of the session, and `step_abandoned`
     * then reported `last_stage: form_submitted` for somebody who never
     * submitted anything.
     *
     * The attempt is worth counting, so it keeps its event and pairs with the
     * `form_error` below. It just no longer borrows the name of the thing
     * that did not happen.
     */
    tracker.track('form_submit_attempted', {
      topics: sel, has_text: !!text.trim(), text_length: text.trim().length, mode,
    });

    {
      if (!emailOk) {
        tracker.track('form_error', { field: 'email', error: 'invalid_email' });
        revealError('Enter a valid email address.');
        return;
      }
      if (!sel.length && !text.trim()) {
        tracker.track('form_error', { field: 'topics_or_text', error: 'nothing_selected' });
        revealError('Pick a topic or describe what you want to find.');
        return;
      }
      if (!consent) {
        tracker.track('form_error', { field: 'consent', error: 'consent_required' });
        revealError('Tick the box to receive the daily update.');
        return;
      }
    }

    /*
     * The feed is built behind the confirmation, which is what the 202 means,
     * so the happy path still does not wait for it. But it used to not wait for
     * ANY outcome, and that hid a total outage: the newsroom form posted
     * `consent: false`, the server rejected every submission with
     * `CAMPAIGN_NO_CONSENT`, and the page said "Demo requested." regardless.
     * Adi submitted twice on 29 October 2026 and was told it worked twice. The
     * failure existed only as an analytics event nobody was watching.
     *
     * So a 4xx is now raced against a short grace period. A rejection is
     * deterministic and arrives immediately: it means this submission will
     * NEVER succeed, however long anyone waits, and the visitor has to be told.
     * Anything slower than the grace period, including every 5xx and every slow
     * network, proceeds optimistically exactly as before, because there the old
     * reasoning still holds and a successful signup must not look like a
     * failure.
     */
    /*
     * Past validation, so this is a real submission: the request is about to
     * go. The stage advances here for the same reason, so the funnel counts
     * people who actually submitted rather than people who pressed a button.
     */
    tracker.advance('form_submitted');
    tracker.track('form_submitted', {
      topics: sel, has_text: !!text.trim(), text_length: text.trim().length, mode,
    });

    setSubmitting(true);
    const base = process.env.REACT_APP_BASE_URL || '/api';
    const posted = axios
      .post(`${base}/public/campaign/visits`, {
        audience: config.audience,
        // Chips are validated against the audience's own list server-side and
        // composed into the brief there, so nothing typed here reaches the
        // model unchecked.
        topics: sel,
        brief: text.trim(),
        email,
        consent,
        landingVariant: 'v3',
        utm: readUtmForSubmit(),
      })
      .then(() => null)
      .catch((err) => err);

    const settled = await Promise.race([
      posted,
      new Promise((resolve) => { setTimeout(() => resolve(PENDING), SUBMIT_GRACE_MS); }),
    ]);
    setSubmitting(false);

    /*
     * THREE outcomes, not two.
     *
     * `posted` resolves to `null` on a 2xx and to the error on a rejection,
     * so `null` is a CONFIRMED success. The old shape tested
     * `settled && settled !== PENDING`, and `null` is falsy, so a confirmed
     * success fell into the branch commented "Still in flight" and was
     * indistinguishable from a request that had not answered yet. The page
     * genuinely could not tell you the server had said yes.
     *
     * That is what made `lead_created` unusable as an ad conversion: it fires
     * on all three outcomes below except the 4xx, and nothing downstream
     * could tell which one it was. Adi's measurement agent flagged exactly
     * this on 3 November 2026 before wiring the LinkedIn conversion.
     */
    const timedOut = settled === PENDING;
    const confirmed = !timedOut && settled === null;
    const failed = !timedOut && !confirmed;

    const status = failed ? settled?.response?.status : null;
    if (failed) {
      const code = settled?.response?.data?.code || 'network_error';
      tracker.track('form_error', { field: 'submit', error: code });
      // Only a 4xx stops the visitor. A 5xx or a dropped connection may still
      // have created the visit, and telling someone it failed when it did not
      // is its own kind of wrong.
      if (status >= 400 && status < 500) {
        revealError(settled?.response?.data?.error || SUBMIT_REJECTED);
        return;
      }
    } else if (timedOut) {
      /*
       * Unknown, not failed. Resolve it late: the handoff below is a
       * client-side route change, so this document and its `dataLayer`
       * outlive the navigation and a late push still lands.
       */
      posted.then((err) => {
        if (err) {
          tracker.track('form_error', {
            field: 'submit', error: err?.response?.data?.code || 'network_error',
          });
          return;
        }
        tracker.track('lead_confirmed', { conversion_type: mode, acknowledged: 'late' });
      });
    }

    tracker.advance('lead_created');
    tracker.track('trial_started', { conversion_type: mode });
    /*
     * `lead_confirmed` is the one to hang an ad conversion on: it fires only
     * when the server has acknowledged the visit, immediately when the answer
     * beat the grace period and late when it did not. `lead_created` keeps
     * its existing optimistic meaning so the product funnel and the screen
     * the visitor sees are unchanged, and now says which it was.
     */
    if (confirmed) {
      tracker.track('lead_confirmed', { conversion_type: mode, acknowledged: 'immediate' });
    }
    tracker.track('lead_created', {
      server_ack: confirmed ? 'confirmed' : (timedOut ? 'pending' : 'error'),
      marketing_consent: consent,
      conversion_type: mode,
      topics: sel,
      has_text: !!text.trim(),
      text_length: text.trim().length,
    });
    // The trial flow's own confirmation page is the designed first screen, so a
    // trial signup leaves this page entirely.
    //
    // The handover is deliberately fire-and-forget, exactly as the inline card
    // was: `setSent(true)` above never waited for the request either, and a
    // signup that 4xx'd is recorded in the catch rather than shown. Moving the
    // screen does not change which outcomes the visitor sees.
    if (onTrialSubmitted) {
      onTrialSubmitted();
      return;
    }

    setSent(true);
    setError('');
  };

  const summary = [...sel, text.trim()].filter(Boolean).join(', ');
  const emailInvalid = !!error && /email/i.test(error);

  /* ── Pieces ─────────────────────────────────────────────────────────── */

  const feedCard = (
    <section style={{
      display: 'flex',
      flexDirection: 'column',
      background: '#fff',
      border: `1px solid ${BORDER}`,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(11,30,63,0.08)',
      overflow: 'hidden',
    }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '16px 24px',
        borderBottom: `1px solid ${BORDER}`,
        fontWeight: 600,
        fontSize: 12,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: SLATE,
      }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 999, background: ORANGE, flexShrink: 0 }} />
        Example feed · Detected by Alchemiq
      </div>

      {/*
        * flex-wrap, gap, padding and overflow-x come from `.lp-catrow`: one
        * sideways-scrolling line on a desktop, wrapped onto several lines on a
        * phone, where a horizontal scroller nested in a vertically scrolling
        * page is an awkward gesture that hides half the categories.
        */}
      <div
        className="lp-catrow"
        style={{
          display: 'flex',
          overflowY: 'hidden',
          borderBottom: `1px solid ${BORDER}`,
          background: SOFT_WHITE,
        }}
      >
        {feedCategories.map((name_) => {
          const on = cat === name_;
          return (
            <button
              key={name_}
              type="button"
              aria-pressed={on}
              onClick={() => {
                tracker.advance('feed_interacted');
                tracker.track('category_selected', { category: name_ });
                setCat(name_);
              }}
              /*
               * No `flexShrink: 0`, it fought the wrap on a phone. The 36px
               * visible height is kept and the 44px touch target comes from
               * the `::before` band in `.lp-catrow button`, so the pill does
               * not have to grow and upset the rhythm of a wrapped row.
               */
              style={{
                whiteSpace: 'nowrap',
                minHeight: 36,
                padding: '0 14px',
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 13,
                color: on ? '#fff' : NAVY,
                border: 0,
                background: on ? ORANGE : PILL_GREY,
              }}
            >
              {name_}
            </button>
          );
        })}
      </div>

      {stories === null && (
        <div style={{ padding: '18px 24px', fontSize: 14, color: SLATE }}>Loading stories…</div>
      )}
      {stories !== null && stories.length === 0 && (
        <div style={{ padding: '18px 24px', fontSize: 14, color: SLATE }}>
          No stories detected in this category in the last 24 hours.
        </div>
      )}
      {(stories || []).map((s, i) => (
        <div
          key={s._id || s.id || i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            padding: '18px 24px',
            borderBottom: '1px solid rgba(60,74,94,0.1)',
          }}
        >
          <span style={{
            flexShrink: 0,
            minWidth: 44,
            textAlign: 'center',
            padding: '8px 0',
            borderRadius: 6,
            background: 'rgba(0,174,239,0.14)',
            color: '#0B8FC4',
            fontWeight: 700,
            fontSize: 14,
            lineHeight: 1,
          }}
          >
            {relativeTime(s.publishedAt || s.dateTimePub) || '·'}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <span style={{
              fontWeight: 700, fontSize: 16, lineHeight: 1.35, color: NAVY, textWrap: 'pretty',
            }}
            >
              {s.title}
            </span>
            {showWhyItMatters && s.whyItMatters && (
              <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                <b style={{ fontWeight: 600, color: NAVY }}>Why it matters:</b>
                {` ${s.whyItMatters}`}
              </span>
            )}
            <span style={{ fontSize: showWhyItMatters ? 12 : 13, lineHeight: 1.5 }}>
              {formatSources(s)}
            </span>
          </div>
        </div>
      ))}

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '16px 24px',
        background: STRIP_GREY,
      }}
      >
        <span style={{ fontSize: 14, color: SLATE }}>
          Your feed is live from the Alchemiq intelligence engine.
        </span>
        <button
          type="button"
          onClick={takeCategory}
          {...orangeHover}
          style={{
            minHeight: 44,
            padding: '0 18px',
            borderRadius: 6,
            border: 0,
            background: ORANGE,
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {`Get a ${cat.toLowerCase()} feed by email →`}
        </button>
      </div>
    </section>
  );

  const topicsBlock = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ fontWeight: 600, fontSize: 14, color: NAVY }}>
        {trialTopicsLabel || topicsLabel}
        <span style={{ fontWeight: 400, color: SLATE }}> (choose up to 2)</span>
        <span aria-hidden="true" style={{ color: ORANGE, fontWeight: 700 }}> *</span>
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {topicChips.map((label) => (
          <Chip
            key={label}
            label={label}
            on={sel.includes(label)}
            disabled={!sel.includes(label) && sel.length >= MAX_TOPICS}
            onClick={() => toggleTopic(label)}
          />
        ))}
      </div>
    </div>
  );

  const freeTextBlock = (
    <Field
      id="lp-text"
      required
      label={trialFreeTextLabel || freeTextLabel}
    >
      <textarea
        id="lp-text"
        className="lp-textarea"
        rows={5}
        value={text}
        onChange={(e) => { tracker.touch('free_text'); setText(e.target.value); setError(''); }}
        placeholder={freeTextPlaceholder}
        /* font-size and line-height come from `.lp-textarea`: 16px on a phone,
           below which iOS zooms the page the moment this takes focus */
        style={{
          minHeight: 120,
          padding: '12px 14px',
          border: `1px solid ${INPUT_BORDER}`,
          borderRadius: 6,
          fontFamily: 'Inter,sans-serif',
          color: NAVY,
          background: '#fff',
          resize: 'vertical',
        }}
      />
    </Field>
  );

  /*
   * Always orange, and not announced as disabled.
   *
   * Adi, 3 November 2026. The greyed state was never a disabled control: the
   * button has always submitted, and submitting with something missing has
   * always named the missing thing. So grey plus `cursor: not-allowed` plus
   * `aria-disabled` described a button that did not exist, and the grey read
   * as "this page is broken" rather than as "you have more to fill in".
   *
   * `aria-disabled` goes with the grey rather than outliving it: a control
   * that responds to a click and announces what it needs is not disabled, and
   * saying otherwise to a screen reader is the same lie the colour told.
   *
   * The `ready` flag went with them. It never gated anything: the submit
   * handler has always validated each field itself and named the first one
   * missing, which is exactly the behaviour this change makes visible.
   */
  const submitButton = (label) => (
    <button
      type="submit"
      onMouseOver={(e) => { e.currentTarget.style.background = ORANGE_HOVER; }}
      onMouseOut={(e) => { e.currentTarget.style.background = ORANGE; }}
      style={{
        minHeight: 56,
        border: 0,
        borderRadius: 6,
        background: ORANGE,
        color: '#fff',
        fontWeight: 600,
        fontSize: 16,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );

  const privacyNote = (
    <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, textAlign: 'center' }}>
      By subscribing, you agree that Alchemiq may process your email address to send you the
      daily update. We will not sell or rent your personal information. We will not share it
      with third parties for their own marketing purposes. You can unsubscribe at any time.
      Read our{' '}
      <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
        Privacy Policy
      </a>.
    </p>
  );

  /*
   * Sits immediately before the submit button in both forms, which is where
   * the handoff puts it. On a phone the submit button is often the only thing
   * on screen when it is pressed, so `revealError` below scrolls this into
   * view rather than leaving the visitor looking at a button that did nothing.
   */
  const errorLine = error
    ? (
      <span ref={errorRef} style={{ fontSize: 14, fontWeight: 500, color: ERROR }}>
        {error}
      </span>
    )
    : null;

  const trialForm = (
    <form ref={formRef} onSubmit={submit} style={formCardStyle} {...formFocusProps}>
      <h2 style={{
        margin: 0, fontWeight: 600, fontSize: 26, letterSpacing: '-0.015em', color: NAVY,
      }}
      >
        Get Alchemiq’s daily news update
      </h2>
      <p style={{ margin: '-12px 0 0', fontSize: 15, lineHeight: 1.5 }}>
        A concise daily selection of stories worth knowing.
      </p>
      <p style={{ margin: '-8px 0 0', fontSize: 13, lineHeight: 1.5, color: SLATE }}>
        <span style={{ color: ORANGE, fontWeight: 700 }}>*</span>
        {' '}Required: your work email, a topic or a description, and your consent to receive
        the update.
      </p>

      <Field id="lp-email" label="Work email address" required>
        <input
          id="lp-email"
          className="lp-input"
          ref={emailRef}
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          aria-required="true"
          value={email}
          onChange={(e) => { tracker.touch('email'); setEmail(e.target.value); setError(''); }}
          placeholder="name@publication.com"
          style={inputStyle(emailInvalid)}
        />
      </Field>

      {topicsBlock}
      {freeTextBlock}

      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        fontSize: 14,
        lineHeight: 1.5,
        color: NAVY,
        cursor: 'pointer',
      }}
      >
        <input
          type="checkbox"
          checked={consent}
          aria-required="true"
          onChange={(e) => {
            tracker.touch('consent');
            tracker.track('consent_changed', { checked: e.target.checked });
            setConsent(e.target.checked);
            setError('');
          }}
          style={{ width: 22, height: 22, margin: 0, flexShrink: 0, accentColor: ORANGE }}
        />
        <span>
          I’d like to receive Alchemiq’s daily news update by email. I understand that I can
          unsubscribe at any time.
          <span aria-hidden="true" style={{ color: ORANGE, fontWeight: 700 }}> *</span>
        </span>
      </label>

      {errorLine}
      {submitButton('Send me the daily update')}

      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, textAlign: 'center' }}>
        Free trial. You won’t be asked for a credit card to begin the trial.
      </p>
      {privacyNote}
    </form>
  );

  const confirmation = (
    <div role="status" style={confirmCardStyle}>
      <h2 style={confirmTitleStyle}>We&rsquo;re building your feed.</h2>
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5 }}>
        {/*
          * Says what actually happens, which changed on 22 Oct 2026 when the
          * immediate send shipped: the first list is now emailed as soon as
          * the feed finishes building, a few minutes from now, and the
          * weekday rhythm starts the following morning.
          *
          * "In a few minutes" rather than a promise of a number, because the
          * build is an LLM pass plus a collection run and the median is about
          * two minutes, not a guarantee. And NOT "we have sent it": a list too
          * thin to be worth sending is held back by
          * CAMPAIGN_INSTANT_SEND_MIN_STORIES, in which case the first email is
          * tomorrow morning's. Claiming a send that may not happen is the
          * failure mode this copy has already had once.
          */}
        Your first list will arrive at <b style={{ color: NAVY }}>{email}</b> in
        a few minutes. After that it comes {CAMPAIGN_SEND_WINDOW_SENTENCE},
        starting tomorrow.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${DIVIDER}` }}>
        <ConfirmRow label="Looking for" value={summary} align="right" />
        <ConfirmRow label="First list" value="In a few minutes" />
        <ConfirmRow label="Then" value={`Every weekday, ${CAMPAIGN_SEND_WINDOW_LABEL}`} />
      </div>
      <button
        type="button"
        onClick={() => { tracker.track('change_search_clicked'); setSent(false); }}
        style={{ ...linkButtonStyle, alignSelf: 'flex-start', border: 0, background: 'none' }}
      >
        Change my search
      </button>
    </div>
  );


  return (
    /*
     * THE DOCUMENT SCROLLS, not this element.
     *
     * The Alchemiq shell is a fixed-viewport app: `.app-container` and
     * `.main-content` are height:100vh with overflow:hidden, because the
     * stories workflow scrolls inside its own panes, so a long document
     * dropped into it is clipped with no scrollbar to hint at it. This page
     * used to answer that by scrolling inside itself, which worked on a
     * desktop and was hostile on a phone: an inner scroller stops iOS Safari
     * collapsing its URL bar, so the visitor permanently loses that band of
     * screen, `100vh` is measured with the bar retracted so the page is taller
     * than what is visible, and a `position:fixed` child of a scrolled box
     * jumps when the software keyboard opens, which is exactly what the sticky
     * bar below is.
     *
     * Instead the shell is released for this route by
     * `.app-container:has(.lp-page)` and `.main-content:has(.lp-page)` in
     * `App.css`, the same pair the public story page and the public
     * suggestions page already use. Hence `className="lp-page"`, which is
     * doing real work and is not a styling hook.
     */
    <div
      className="lp-page"
      style={{
        fontFamily: 'Inter,sans-serif',
        color: SLATE,
        background: SOFT_WHITE,
      }}
    >
      {/* padding and, on newsroom, the actions gap come from `.lp-header` */}
      <header
        className="lp-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: NAVY,
        }}
      >
        {/*
          * `.lp-header-logo` lets this shrink when the header runs out of room.
          * It was added for the newsroom header, which also carried a "Request
          * a demo" button until that branch was removed on 29 Oct 2026. Kept
          * because a long website label can still crowd the logo at 360px. See
          * the stylesheet for why it is flexbox rather than a measured
          * max-width.
          */}
        <img
          className="lp-header-logo"
          src="/textOnlyLogoOrr-01.png"
          alt="Alchemiq"
          style={{ height: 28 }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {/* gap comes from `.lp-header-actions`: 24 desktop, 12 on a phone, so
            the logo and the link still share one line at 360px */}
        <div className="lp-header-actions" style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
          {/*
            * Per-audience now. It was hardcoded to the homepage, so the
            * marketing page pointed there too instead of at /solutions/pr.
            *
            * The label is CONTENT rather than style, and only the newsroom
            * page shortens it, so both strings are rendered and the stylesheet
            * picks one. Doing it that way rather than with a JavaScript width
            * check keeps the markup correct for a prerendered page and avoids
            * a first-paint flash. `display:none` leaves the unused one out of
            * the accessibility tree, so the address is not announced twice.
            */}
          <a
            href={websiteUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              whiteSpace: 'nowrap',
              fontWeight: 600,
              fontSize: 14,
              color: SOFT_WHITE,
              textDecoration: 'none',
            }}
          >
            <span className="lp-web-full">{websiteLabel}</span>
            <span className="lp-web-short">{websiteLabelPhone || websiteLabel}</span>
          </a>
        </div>
      </header>

      {/* padding and gap come from `.lp-main` */}
      <main
        className="lp-main"
        style={{
          maxWidth: 1240,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <section style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,420px),1fr))',
          gap: 40,
          alignItems: 'end',
        }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              whiteSpace: 'nowrap',
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: NAVY,
              background: 'rgba(0,174,239,0.14)',
              borderRadius: 999,
              padding: '6px 14px',
            }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, background: TECH_BLUE }} />
              {heroPill}
            </span>
            <h1 style={{
              margin: 0,
              fontWeight: 600,
              fontSize: 'clamp(40px,5.4vw,64px)',
              lineHeight: 1.08,
              letterSpacing: '-0.025em',
              color: NAVY,
              textWrap: 'balance',
            }}
            >
              {h1[0]}
              <span style={{ color: ORANGE }}>{h1[1]}</span>
            </h1>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{
              margin: 0, fontSize: 17, lineHeight: 1.6, textWrap: 'pretty', color: NAVY,
            }}
            >
              {introBold
                ? (
                  <>
                    {intro.split(introBold)[0]}
                    <b>{introBold}</b>
                    {intro.split(introBold)[1]}
                  </>
                )
                : intro}
            </p>
            {/*
              * Marketing carries no `noteBold`/`noteRest`, so this box used to
              * render as an EMPTY bordered strip on that page. Guarded now, and
              * the jump-to-form button falls out of the box rather than
              * disappearing with it.
              */}
            {(noteBold || noteRest) ? (
              <div style={{
                background: '#fff',
                border: `1px solid ${DIVIDER}`,
                borderLeft: `3px solid ${TECH_BLUE}`,
                borderRadius: 6,
                padding: '14px 18px',
                fontSize: 15,
                lineHeight: 1.5,
                color: NAVY,
              }}
              >
                <b style={{ color: NAVY }}>{noteBold}</b>{noteRest}
                {/*
                  * Adi, 1 November 2026: reaching the form required scrolling,
                  * and she asked for a button "in the top bar, or in that box".
                  * The box, by Liad's choice: the header already lost a button
                  * once for crowding at 360px, and an e2e test now asserts it
                  * holds none.
                  *
                  * Styles stay inline because this box has no class and nothing
                  * about the button changes at the phone breakpoint, which is
                  * exactly the condition the inline/CSS partition rule sets.
                  */}
                <JumpToForm label={noteCta} onClick={() => goToForm('note')} />
              </div>
            ) : (
              <JumpToForm label={noteCta} onClick={() => goToForm('note')} />
            )}
          </div>
        </section>

        {proof && (
          <section style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))',
            background: '#fff',
            border: `1px solid ${BORDER}`,
            borderRadius: 8,
          }}
          >
            {proof.map(([n, l], i) => (
              <div
                key={n}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  padding: '24px 28px',
                  borderLeft: i === 0 ? 0 : `1px solid ${DIVIDER}`,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 30, color: NAVY }}>{n}</span>
                <span style={{ fontSize: 14, lineHeight: 1.5, color: SLATE }}>{l}</span>
              </div>
            ))}
          </section>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,400px),1fr))',
          gap: 40,
          alignItems: 'start',
        }}
        >
          {feedCard}
          <section style={{ position: 'sticky', top: 24, alignSelf: 'start' }}>
            {sent ? confirmation : trialForm}
          </section>
        </div>

        <section style={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid rgba(60,74,94,0.18)',
          borderBottom: '1px solid rgba(60,74,94,0.18)',
        }}
        >
          <button
            type="button"
            aria-expanded={faqOpen}
            onClick={() => {
              tracker.track('faq_toggled', { scope: 'section', open: !faqOpen });
              setFaqOpen(!faqOpen);
            }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              width: '100%',
              minHeight: 72,
              padding: '16px 0',
              border: 0,
              background: 'none',
              textAlign: 'left',
              color: NAVY,
            }}
          >
            <span style={{
              fontWeight: 600, fontSize: faqHeadingSize || 26, letterSpacing: '-0.015em',
            }}
            >
              {faqHeading || 'Want to know more?'}
            </span>
            <span style={{ fontSize: 32, lineHeight: 1, color: ORANGE }}>
              {faqOpen ? '–' : '+'}
            </span>
          </button>
          {faqOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 8 }}>
              {faq.map(([q, a], i) => (
                <div key={q} style={{ borderTop: `1px solid ${DIVIDER}` }}>
                  <button
                    type="button"
                    aria-expanded={open === i}
                    onClick={() => {
                      tracker.track('faq_toggled', { scope: 'question', question: q, open: open !== i });
                      setOpen(open === i ? -1 : i);
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                      width: '100%',
                      minHeight: 56,
                      padding: '12px 0',
                      border: 0,
                      background: 'none',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 17,
                      color: NAVY,
                    }}
                  >
                    <span>{q}</span>
                    <span style={{ fontSize: 24, lineHeight: 1, color: ORANGE }}>
                      {open === i ? '–' : '+'}
                    </span>
                  </button>
                  {open === i && (
                    <p style={{
                      margin: '0 0 20px', fontSize: 15, lineHeight: 1.6, maxWidth: 760,
                    }}
                    >
                      {a}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* padding comes from `.lp-contact`; the existing wrap already drops
            the button under the text on a narrow screen */}
        <section
          className="lp-contact"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            background: NAVY,
            borderRadius: 8,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 20, color: SOFT_WHITE }}>
              Prefer to talk to a person?
            </span>
            <span style={{ fontSize: 15, color: SOFT_WHITE }}>Write to us directly.</span>
          </div>
          <a
            href={`mailto:${SUPPORT}`}
            {...orangeHover}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 48,
              padding: '0 20px',
              borderRadius: 6,
              background: ORANGE,
              color: '#fff',
              fontWeight: 600,
              fontSize: 15,
              textDecoration: 'none',
            }}
          >
            {SUPPORT}
          </a>
        </section>
      </main>

      {/* Rendered inside the page, so the unknown-slug redirect path in
          LandingRoute never shows a cookie bar to someone being bounced to
          another host. `bottomOffset` keeps it clear of the mobile sticky CTA,
          measured rather than guessed. */}
      <LandingConsent
        visible={cookieBar.visible}
        accept={cookieBar.accept}
        decline={cookieBar.decline}
      />

      {/*
        * Reserves the bar's height so it never covers the contact band.
        *
        * Keyed to `barActive`, NOT to `barVisible`: while a field has focus the
        * bar is hidden but its space must stay reserved, or focusing a field
        * would shrink the document under a smooth scroll already in flight.
        */}
      <div style={{ height: barActive ? Math.max(STICKY_BAR_FALLBACK_H, barH) : 0 }} />
      <div
        ref={barRef}
        className="lp-sticky"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          display: barVisible ? 'block' : 'none',
          /*
           * `max()` rather than `calc(12px + env(...))`.
           *
           * With the default `viewport-fit=auto` iOS lays the page out inside
           * the safe area, so `bottom: 0` is already above the home indicator
           * and `env(safe-area-inset-bottom)` is 0, correctly, because
           * nothing needs compensating. Adding `viewport-fit=cover` to make
           * that value non-zero would extend the layout under the indicator
           * for the whole app, including the fixed-viewport stories workflow,
           * which has no inset handling at all.
           *
           * So this is a no-op today and becomes right on its own if `cover`
           * is ever adopted deliberately. `calc()` would have added the inset
           * ON TOP of the padding and over-padded the bar in that case.
           */
          padding: '12px 16px max(12px, env(safe-area-inset-bottom))',
          background: '#fff',
          borderTop: `1px solid ${BORDER}`,
          boxShadow: '0 -4px 16px rgba(11,30,63,0.10)',
        }}
      >
        <button
          type="button"
          onClick={() => goToForm('sticky')}
          {...orangeHover}
          style={{
            width: '100%',
            minHeight: 52,
            border: 0,
            borderRadius: 6,
            background: ORANGE,
            color: '#fff',
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          {'Get the daily update'}
        </button>
      </div>
    </div>
  );
}

/**
 * The jump-to-form button, inside the hero note box or on its own.
 *
 * One component for both placements so the two cannot drift. It carries no
 * class: nothing about it changes at the phone breakpoint, which is the
 * condition the inline-style carve-out at the top of this file sets out.
 */
function JumpToForm({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'block',
        marginTop: 12,
        minHeight: 44,
        padding: '0 18px',
        borderRadius: 6,
        border: 0,
        background: ORANGE,
        color: '#fff',
        fontWeight: 600,
        fontSize: 14,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
    >
      {label || 'Get my feed \u2192'}
    </button>
  );
}

function ConfirmRow({ label, value, align }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 16,
      padding: '12px 0',
      borderBottom: `1px solid ${DIVIDER}`,
      fontSize: 14,
    }}
    >
      <span>{label}</span>
      <b style={{ color: NAVY, textAlign: align || 'left' }}>{value}</b>
    </div>
  );
}

const formCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  background: '#fff',
  border: `1px solid ${BORDER}`,
  borderTop: `4px solid ${ORANGE}`,
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(11,30,63,0.12)',
  padding: 28,
};

const confirmCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  background: '#fff',
  border: `1px solid ${BORDER}`,
  borderTop: `4px solid ${TECH_BLUE}`,
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(11,30,63,0.12)',
  padding: 28,
};

const confirmTitleStyle = {
  margin: 0,
  fontWeight: 600,
  fontSize: 26,
  letterSpacing: '-0.015em',
  color: NAVY,
};

const linkButtonStyle = {
  minHeight: 44,
  padding: '0 4px',
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 600,
  fontSize: 14,
  color: NAVY,
  textDecoration: 'underline',
  cursor: 'pointer',
};
